import React, { useMemo, useState } from 'react';
import {
  CountryOption,
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import { Destination, TripRequestStatus } from '@marageti/z4-sdk/lib/travel';
import { useNavigate } from 'react-router-dom';
import TripRequestsIncomingView from './incoming-view';
import { useAuthStore } from '../../../store';
import useTripRequestQuery from '../../../hooks/use-url-for-search-query';
import useTripRequestSearch from '../../../hooks/use-trip-request-search';
import useIsAdmin from '../../../hooks/use-is-admin';
import { tagOptions, sortByOptionsIncoming, sortByOptionPecv } from '../options';

const sortByValues: { [key: string]: any } = {
  totalBudget: { field: 'totalBudget', order: 'desc' },
  pppd: { field: 'pppd', order: 'desc' },
  departureDate: { field: 'departureDate', order: 'asc' },
  createdAtDesc: { field: 'createdAt', order: 'desc' },
  createdAtAsc: { field: 'createdAt', order: 'asc' },
  initialPecv: { field: 'initialPecv', order: 'desc' },
};

interface DefaultSearch {
  [key: string]: string | string[];
  sort: string;
  order: string;
  destinations: string[];
  assignee: string;
  tag: string;
  status: string[];
}

const defaultSearch: DefaultSearch = {
  sort: 'createdAtDesc',
  order: sortByValues.createdAtDesc.order,
  destinations: [],
  assignee: 'UNASSIGNED',
  tag: 'ALL',
  status: [TripRequestStatus.VerifiedStatus],
};

const TripRequestsIncoming = () => {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { loggedInTeamMember } = useAuthStore();
  const isAdmin = useIsAdmin();
  const sortByOptions = useMemo(
    () => (isAdmin ? [...sortByOptionsIncoming, sortByOptionPecv] : sortByOptionsIncoming),
    [isAdmin],
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [queryValues, updateURLQuery] = useTripRequestQuery<DefaultSearch>(defaultSearch);

  const {
    destinations,
    sort,
    status,
    tag,
  } = queryValues;
  const sortConfig = sortByValues[sort] ?? { field: sort, order: queryValues.order };
  const {
    tripRequests,
    error,
    hasMore,
    isLoading,
    loadMore,
    totalHits,
  } = useTripRequestSearch({
    ...queryValues,
    sort: sortConfig.field,
    order: sortConfig.order,
  });

  const { data: destinationsList, isLoading: isLoadingDestinations } = useFetch<Destination[]>(apiClient.destinationsClient.getDestinations, [{ params: { limit: 1000, all: true } }], true);

  const formattedTrCount = new Intl.NumberFormat('en-US').format(totalHits || 0);

  const destinationOptions = useMemo(() => destinationsList?.map((destination) => ({
    label: destination.name,
    value: destination.id,
  })).sort((a, b) => a.label.localeCompare(b.label)), [destinationsList]) || [];
  // Get selected destination options from destinations
  const selectedDestinations = destinations.length > 0 && destinationOptions.length > 0
    ? destinationOptions.filter((d) => destinations.includes(d.value)) : [];
  const assignToSelf = async (id: string) => {
    try {
      if (id && loggedInTeamMember) {
        await apiClient.tripRequestsClient.putTripRequestAssignment(id, {
          id,
          assignedToName: loggedInTeamMember.fullName,
          assignedTo: loggedInTeamMember.id,
        });
      }
    } catch (e) {
      console.error('Error assigning trip request:', e);
    }
  };

  const getNextAvailable = async () => {
    if (tripRequests && tripRequests.length > 0) {
      setIsSubmitting(true);
      const firstTripRequestId = tripRequests[0].id;

      try {
        await assignToSelf(firstTripRequestId);
        setIsSubmitting(false);
        navigate(`/trip-requests/${firstTripRequestId}`);
      } catch (e) {
        setIsSubmitting(false);
        console.error('Error getting the next available:', e);
      }
    }
  };

  const onDestinationChange = (e: React.SyntheticEvent<Element, Event>, newValue: CountryOption[]) => {
    updateURLQuery({ destinations: newValue.map((n) => n.value) });
  };

  const onTagChange = (selectedTag: string) => {
    updateURLQuery({ tag: selectedTag });
  };

  const onSortChange = (selectedSort: string) => {
    updateURLQuery({
      sort: selectedSort,
      order: sortByValues[selectedSort].order,
    });
  };

  const onStatusChipChange = (selectedChips: string[]) => {
    updateURLQuery({ status: selectedChips });
  };

  return (
    <TripRequestsIncomingView
      error={error}
      getNextAvailable={getNextAvailable}
      hasMoreItems={hasMore}
      isSubmitting={isSubmitting}
      isLoading={isLoading}
      items={tripRequests}
      loadMore={loadMore}
      totalHits={formattedTrCount}
      destinationOptions={destinationOptions}
      isLoadingDestinations={isLoadingDestinations}
      onDestinationChange={onDestinationChange}
      selectedDestinations={selectedDestinations}
      onSortChange={onSortChange}
      sortBy={sort}
      sortByOptions={sortByOptions}
      onTagChange={onTagChange}
      selectedTag={tag}
      tagOptions={tagOptions}
      selectedStatusChips={status}
      onStatusChipChange={onStatusChipChange}
    />
  );
};

export default TripRequestsIncoming;
